// Viewer-review help follows the real viewer rail, per-subview approval store,
// automatic next-pending navigation, stage availability, and run release gate.
import type { HelpArticle } from "../../types";
import { PROJECT_HELP_PROJECT } from "../../../project/projectPreviewFixtures";

export const viewerReviewArticle: HelpArticle = {
  id: "viewer-review",
  title: "Reviewing in the viewer",
  description:
    "Navigate the identified DXF, approve every review view, understand blocked stages, and release a real run from Setup into Loading.",
  tags: ["viewer", "review", "approval", "stages", "DXF", "loading model"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "The viewer is where a pipeline result becomes reviewable geometry. The canvas shows the DXF, the left rail partitions the `building_review` output into named story views, and the Review section records approval for each subview. Approving one view does not imply that the rest of the floor plan is accepted.",
    },

    { kind: "heading", level: 2, text: "Orient yourself", id: "orientation" },
    { kind: "where", path: ["Design projects", PROJECT_HELP_PROJECT.name, "Viewer", "Floor plan"] },
    {
      kind: "paragraph",
      text: "The top bar keeps project context and global actions in one strip: project selection, undo and redo, drafting mode, print preview, reset, settings, help, and account controls. The stage breadcrumb names the pipeline sequence. On a two-story plan it can show **Floor plan → Loading → Roof → Ceiling → Floor → Walls → Final Layout**; the Floor stage is omitted for a one-story plan.",
    },
    {
      kind: "paragraph",
      text: "Only stages generated by the run are available. A later breadcrumb can be visible but blocked, with its explanation attached, until the active review gate is released. In the Riverside fixture, selecting a framing or wall story subview changes the geometry under review in the canvas.",
    },
    {
      kind: "shot",
      shotId: "viewer-review-shell",
      caption: "The real viewer shell: project and run context, stage sequence, review rail, and DXF canvas.",
    },

    { kind: "heading", level: 2, text: "Views, layers, and display controls", id: "views" },
    {
      kind: "paragraph",
      text: "The Riverside schema-3 fixture carries one `building_review` result. The adapter partitions its subviews by `focus`: framing, beams, voids, and attic join **Framing**; walls join **Walls**; roof joins **Roofing**. This fixture contains first- and second-story framing plus first- and second-story walls, yielding four approval steps. The rail also carries display controls for dimensions, visibility, and layers.",
    },
    {
      kind: "definition-list",
      items: [
        { term: "building_review", definition: "The single generated review result used by the schema-3 fixture." },
        { term: "Framing", definition: "The group formed from framing, beam, void, and attic-focused subviews." },
        { term: "Walls", definition: "The group formed from wall-focused subviews." },
        { term: "Layer visibility", definition: "Display state for DXF layers; it changes what is shown, not the approval state stored for a subview." },
      ],
    },
    {
      kind: "shot",
      shotId: "viewer-review-views",
      caption: "The one building review is partitioned into Framing and Walls story subviews beside the display controls used to inspect them.",
    },

    { kind: "heading", level: 2, text: "Approve every review step", id: "approvals" },
    {
      kind: "paragraph",
      text: "The Review header reports approved steps over total steps. **Approve** records the active story subview, then advances the rail to the next pending review step. **Approve all** records all four reviewable subviews in this Setup result. When a step is already approved, **Undo** removes that approval; **Next pending** finds the next outstanding step without changing the current approval.",
    },
    {
      kind: "steps",
      steps: [
        {
          title: "Select a generated story subview",
          body: "Choose the first- or second-story row under Framing or Walls. That row establishes the review step shown in the canvas and Review section.",
        },
        {
          title: "Inspect the canvas",
          body: "Use the dimension, visibility, and layer controls to verify the identified geometry for that subview. Approval belongs to the active review step, not to the current zoom or display state.",
        },
        {
          title: "Approve and follow the advance",
          body: "Choose **Approve**. The counter increments and the next pending subview becomes active automatically, keeping the review moving without hiding what remains.",
        },
        {
          title: "Resolve the remaining count",
          body: "Repeat until approved equals total. If a decision changes, return to that view and use **Undo**; the stage returns to a pending state.",
        },
      ],
    },
    {
      kind: "shot",
      shotId: "viewer-review-progress",
      caption: "One approval in the deterministic Riverside fixture increments the counter and selects the next pending view.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "The figure demonstrates the interaction",
      body: "The Riverside viewer capture is a deterministic fixture used to show approve-and-advance. A production handoff is scoped to its real project and pipeline run, and its approvals persist against that run's review scope.",
    },

    { kind: "heading", level: 2, text: "Release the Setup gate", id: "release" },
    {
      kind: "paragraph",
      text: "On a real project run, approving every Setup view unlocks **Run Loading Model**. The confirmation releases the parked Setup gate, and the viewer polls the project pipeline until Loading succeeds or the run reports a failure. The current Site Setup guard reads the viewer's bundled `project-input-context.json` fixture, not the active project's portal setup record; its missing-field warning demonstrates the local validation interface only. The standalone fixture uses **Complete setup** as a client-side demonstration; it does not release a server pipeline gate.",
    },
    {
      kind: "paragraph",
      text: "Undoing an approval after full approval revokes readiness. The stage cannot stay marked ready while one of its review keys is pending again. The breadcrumb continues to show only the stage payloads the run has generated.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Approval is a stage decision",
      body: "Do not use Approve all as a navigation shortcut. It records an engineering review decision for every generated step in the active stage and can unlock the next pipeline action.",
    },

    { kind: "related", topics: ["floor-plans", "project-setup", "hazard-reports"] },
  ],
};
